import type {
  ExternalRefundConfirmationRequestType,
  ExternalRefundResponseType,
} from "@sellar-io/api-v2/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useFirebaseUser } from "../../../useFirebaseUser.shared";
import { useSellarCurrentBusiness } from "../../../useSellarCurrentBusiness.shared";
import { isApiV2ContextReady } from "../apiV2QueryScope";
import {
  apiV2AssertResponse,
  apiV2Request,
  getDefaultQueryOptionsV2,
} from "../apiV2Request";
import { ServerStateKeys } from "../ServerStateKeys";

export function useOrderExternalRefundConfirmation(orderId: number) {
  const { user } = useFirebaseUser();
  const { business } = useSellarCurrentBusiness();

  return useQuery({
    queryKey: [
      user?.uid,
      business?.id,
      ServerStateKeys.OrderExternalRefundConfirmation,
      orderId,
    ],
    queryFn: ({ signal }) =>
      apiV2Request({
        user,
        business,
        method: "get",
        path: `order-external-refund-confirmations/${orderId}`,
        signal,
      }).then(
        (response) =>
          apiV2AssertResponse<ExternalRefundResponseType>(response).data,
      ),
    ...getDefaultQueryOptionsV2<ExternalRefundResponseType>({
      context: { scope: "supplierMember", user, business },
      // Submission waits for a fresh balance; fail promptly instead of locking
      // the confirmation form behind the default unlimited network retries.
      options: { staleTime: 0, retry: false },
    }),
  });
}

export function useRecordOrderExternalRefundConfirmation(orderId: number) {
  const { user } = useFirebaseUser();
  const { business } = useSellarCurrentBusiness();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ExternalRefundConfirmationRequestType) => {
      if (!isApiV2ContextReady({ scope: "supplierMember", user, business })) {
        throw new Error(
          "Active supplier membership required. Refresh and try again.",
        );
      }
      const response = await apiV2Request({
        user,
        business,
        method: "post",
        path: `order-external-refund-confirmations/${orderId}`,
        data,
      });
      return apiV2AssertResponse<ExternalRefundResponseType>(response).data;
    },
    onSuccess: async (response) => {
      queryClient.setQueryData(
        [
          user?.uid,
          business?.id,
          ServerStateKeys.OrderExternalRefundConfirmation,
          orderId,
        ],
        response,
      );
      await Promise.all(
        [
          ServerStateKeys.OrderExternalRefundConfirmation,
          ServerStateKeys.Orders,
          ServerStateKeys.OrdersLegacy,
          ServerStateKeys.Payments,
          ServerStateKeys.Refunds,
          ServerStateKeys.OrderEvents,
        ].map((key) =>
          queryClient.invalidateQueries({
            queryKey: [user?.uid, business?.id, key],
          }),
        ),
      );
    },
  });
}
